import { api, ApiError } from "../../api";
import { isAbort, type Lifecycle } from "../shared/lifecycle";
import { parseFacts, type FactsView } from "./factsModel";

/**
 * What every card on the facts page shares: the client id, the lifecycle (nothing paints after the person leaves), the current approved
 * facts and their REVISION, and a small change bus. The revision is what edits send back as `ifRevision`, so every card that edits
 * facts reads it from here and every successful write refreshes it.
 */
export interface Workspace {
  clientId: string;
  life: Lifecycle;
  facts: FactsView | null;
  factsError: string | null;
  /** Reads GET /facts again. Resolves with the new view (or null when it failed). */
  refreshFacts(): Promise<FactsView | null>;
  /** Cards subscribe to be repainted when the facts (and so the revision and the counters) changed. */
  onFacts(fn: () => void): void;
  /** Cards subscribe to learn that something was written (history should reload). */
  onWrite(fn: () => void): void;
  /** A card wrote something: refresh the facts and tell the others. */
  wrote(): Promise<void>;
}

export function createWorkspace(clientId: string, life: Lifecycle): Workspace {
  const factsListeners: (() => void)[] = [];
  const writeListeners: (() => void)[] = [];
  let seq = 0;
  const ws: Workspace = {
    clientId,
    life,
    facts: null,
    factsError: null,
    onFacts: (fn) => void factsListeners.push(fn),
    onWrite: (fn) => void writeListeners.push(fn),
    async refreshFacts() {
      const mine = ++seq;
      try {
        const view = parseFacts(await api<unknown>(`/v1/clients/${encodeURIComponent(clientId)}/facts`));
        if (mine !== seq || !life.alive()) return ws.facts;
        if (!view) {
          ws.factsError = "The server sent facts this page does not understand.";
        } else {
          ws.facts = view;
          ws.factsError = null;
        }
      } catch (e) {
        if (isAbort(e) || mine !== seq || !life.alive()) return ws.facts;
        ws.factsError = e instanceof ApiError ? (e.status === 404 ? "This client was not found." : `The facts could not be loaded (HTTP ${e.status}).`) : "Couldn't reach the server.";
      }
      factsListeners.forEach((fn) => fn());
      return ws.facts;
    },
    async wrote() {
      await ws.refreshFacts();
      writeListeners.forEach((fn) => fn());
    },
  };
  return ws;
}

export const enc = encodeURIComponent;
